'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { getSiteConfig } from '@shared/config';
import { useTranslations } from 'next-intl';
import { useEffect } from 'react';
import { FormProvider, useForm, type Resolver } from 'react-hook-form';
import { MultistepForm } from '../../../../form-controls/MultistepForm/MultistepForm';
import { MyAccountSheet } from '../../../MyAccountSheet/MyAccountSheet';
import type { SelfExclusionPeriod } from '../../types';
import { ExclusionStep, getExclusionFormSchema } from './steps/ExclusionStep';

type ExclusionFormData = {
  period: SelfExclusionPeriod | '';
  confirmExclusion: boolean;
};

type SelfExclusionSheetProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  trigger: React.ReactNode;
};

const SelfExclusionSheet = ({ open, onOpenChange, trigger }: SelfExclusionSheetProps) => {
  const t = useTranslations('my_account.safer_gambling.self_exclusion');
  const { selfExclusionPeriods, preselectBreakToolPeriod, resetBreakToolFormOnClose } = getSiteConfig().SAFER_GAMBLING;

  const getDefaultValues = (): ExclusionFormData => ({
    period: preselectBreakToolPeriod ? selfExclusionPeriods[0] : '',
    confirmExclusion: false,
  });

  const form = useForm<ExclusionFormData>({
    resolver: zodResolver(getExclusionFormSchema(t, selfExclusionPeriods)) as Resolver<ExclusionFormData>,
    defaultValues: getDefaultValues(),
  });

  useEffect(() => {
    if (!open && resetBreakToolFormOnClose) {
      form.reset(getDefaultValues());
    }
  }, [open, resetBreakToolFormOnClose, preselectBreakToolPeriod, selfExclusionPeriods, form]);

  const steps = [
    {
      id: 'exclusion',
      component: <ExclusionStep />,
      schema: getExclusionFormSchema(t, selfExclusionPeriods),
    },
  ];

  return (
    <MyAccountSheet
      open={open}
      onOpenChange={onOpenChange}
      trigger={trigger}
      title={t('title')}
      contentClassName="p-0 flex flex-col"
      data-track-feature="My Account"
      data-track-container="Self Exclusion Set"
    >
      <FormProvider {...form}>
        <MultistepForm steps={steps} showProgressBar={false} showNavigation={false} className="flex flex-col flex-1" />
      </FormProvider>
    </MyAccountSheet>
  );
};

export { SelfExclusionSheet };
export type { ExclusionFormData };
